import { ftLdTsEnrichInventory } from '../port/k5-render/ld_ts_mount';
import type { InventoryRenderDeps } from '../port/k5-render/ld_ts_mount';
import type { PortEnv } from '../port-contract';
import { nativePhpJson } from '../php-compat/json';
import { at, entries } from '../port/k5-render/support';
import type { Row as RenderRow } from '../port/k5-render/support';
import { createInventoryEconomyReader } from './inventory-economy-reader';
import { readInventoryField } from './inventory-raw-readers';
import { readItemField } from './item-field-reader';
import { fromBaseRecord } from './from-base-context';
import { phpJsonTruthy } from './php-json-values';

type Row = Record<string, unknown>;
const native = (row: unknown): Row => nativePhpJson(row) as Row;

/** Wire K5's accepted enrichment onto the freshly browser-compiled tree. */
export function prepareInventoryDisplay(host: Row, inventory: Row[], thumbnail: (row: Row) => string | null): void {
    // K5 replaces entry/slot wrappers and never writes their item_arr. The
    // economy and field readers only return values; thumbnail receives a fresh
    // copy so a callback cannot invalidate the snapshot used by another read.
    // This cache lives for one synchronous render, never across host mutations.
    const snapshots = new WeakMap<object, Row>();
    const snapshot = (item: unknown): Row => {
        if (item === null || typeof item !== 'object') return native(item);
        let value = snapshots.get(item);
        if (!value) { value = native(item); snapshots.set(item, value); }
        return value;
    };
    const economy = createInventoryEconomyReader({ resolveInventoryInstance: () => null });
    const equipped: Row[] = [], unequipped: Row[] = [];
    for (const row of inventory) (phpJsonTruthy(readInventoryField(row, 'equipped')) ? equipped : unequipped).push(row);
    const bucket = fromBaseRecord(host.item_layer_types) ? host.item_layer_types : (host.item_layer_types = {}) as Row;
    bucket.inventory_equipped = equipped.map(row => ({ item_arr: row }));
    bucket.inventory_unequipped = unequipped.map(row => ({ item_arr: row }));
    const unavailable = (): never => { throw new Error('unported:inventory-display-static-definition'); };
    const env: PortEnv<InventoryRenderDeps> = {
        globals: {}, functionStatics: {}, rand: unavailable, now: () => 0, nowMicro: () => 0, out: () => {},
        uuid: unavailable, uniqid: unavailable, exit: unavailable, warn: () => {}, randomBytes: unavailable,
        gmdate: unavailable, hrtime: () => 0,
        deps: {
            'CardListItemLayer::parseValue': entry => { const item = at(entry as Row, 'item_arr'); return item && typeof item === 'object' ? { item_arr: item } : null; },
            'CardListItemLayer::getCostGp': item => economy(snapshot(item), 'cost', 'total_gp'),
            list_display_inventory_thumbnail_url: item => thumbnail(structuredClone(snapshot(item))),
            item_field: (item, name) => readItemField(snapshot(item), name),
            ft_creature_worn_equipped_render_arr: unavailable, build_static_item_creature_equipped_inventory: unavailable,
            ft_build_referenced_names_map: unavailable,
        },
    };
    // The shared renderer reads only these three slots. Passing the whole host
    // also copies inventory, encounter members and catalogs, then discards them.
    const slots = ['inventory_equipped', 'inventory_unequipped', 'spell_material_item_id'];
    const selected: Row = { item_layer_types: Object.fromEntries(slots.filter(name => name in bucket).map(name => [name, bucket[name]])) };
    for (const name of slots) if (name in host) selected[name] = host[name];
    const ref: { value: Parameters<typeof ftLdTsEnrichInventory>[0]['value'] } = { value: selected };
    // K5 already copies every wrapper it replaces. Its ordinary PHP callers
    // retain deep value copies; here pure deps plus the final native copy detach
    // the result once, instead of repeatedly cloning each untouched item tree.
    ftLdTsEnrichInventory(ref, env, value => value);
    host.item_layer_types = native({ ...bucket, ...Object.fromEntries(entries(at(ref.value, 'item_layer_types') as RenderRow)) });
}
